import React from 'react';
import { Button } from 'antd';
import { formatCheckedDate } from '../derive';
import { CARD_UPDATE_COPY, CHOOSE_PROVIDER_COPY, GAP_COPY, TEAM_REQUEST_COPY, TEMPLATE_GAP_COPY } from '../copy';
import s from '../s.module.less';

// 琥珀条专用的实心警告三角（与 UnmappedPayerCard 的 WARN_ICON 同一枚）：填充色 + 白色笔画，
// #d97706 比正文的 #92400e 亮一档（同原型）
const WarnIcon: React.FC = () => (
    <svg viewBox="0 0 20 20" fill="none" style={{ color: '#d97706' }}>
        <path
            d="M9.14 2.88a1 1 0 0 1 1.72 0l8.14 14.12a1 1 0 0 1-.86 1.5H1.86a1 1 0 0 1-.86-1.5z"
            fill="currentColor"
        />
        <path d="M10 7.4v4" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
        <circle cx="10" cy="14.7" r="1.05" fill="#fff" />
    </svg>
);

const RefreshIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <polyline points="21 3 21 9 15 9" />
    </svg>
);

// 蓝条的圆圈 i：实心，填充跟随 .ownerNote 的字色（与琥珀条的实心三角同构）
const InfoIcon: React.FC = () => (
    <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="currentColor" />
        <circle cx="10" cy="6.1" r="1.05" fill="#fff" />
        <path d="M10 9v5" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
);

// ── practice 视图：还没有人被选中（原型 09/15）──
// check 要以某位 provider 的 payer ID 发出，患者从未被查过时没有可复用的身份，必须由人来挑。
// 蓝而不是琥珀：没有出错，只是少一个选择，而做出选择的地方就在上面的卡片里
export const ChooseProviderNote: React.FC = () => (
    <div className={s.ownerNote}>
        <InfoIcon />
        <span className={s.noteBody}>{CHOOSE_PROVIDER_COPY}</span>
    </div>
);

// ── practice gap 横幅（payerIdMapped=false / billingNpi 未设时；「这是关于你的练所，不是这个患者」）──
// npi 变体（member 专属，蓝）无按钮：billing NPI 是 practice 级，member 做不了这件事；
// payer 变体按角色分两版（2026-09-10 拍板）：member 蓝色 + Message owner（不自填，改动集中经 owner）；
// owner/solo 琥珀色 + Set it up（进 review grid 自己补，不自动跑 check）
export const PracticeGapNote: React.FC<{
    variant: 'npi' | 'payer';
    // payer 变体的角色形态；npi 变体恒为 member 蓝条
    canSetUp?: boolean;
    carrier?: string;
    state?: string;
    onMessageOwner?: () => void;
    onSetUp?: () => void;
}> = ({ variant, canSetUp, carrier, state, onMessageOwner, onSetUp }) => {
    // 琥珀态 = payer 变体的 owner/solo 版；其余（member 的 payer 条、npi 条）走蓝
    const amber = variant === 'payer' && !!canSetUp;
    return (
        <div
            className={amber ? s.updateNote : s.ownerNote}
            // payer 变体与 TeamRequestBanner 同构：图标/按钮相对多行文字垂直居中
            style={variant === 'payer' ? { alignItems: 'center' } : undefined}
        >
            {amber ? <WarnIcon /> : <InfoIcon />}
            <span className={s.noteBody}>
                {variant === 'npi' ? GAP_COPY.npiMember : GAP_COPY.payerGap(carrier || '', state || '')}
            </span>
            {/* 与 TeamRequestBanner 同构：单按钮直接做 flex 子元素靠右，不套列容器 */}
            {variant === 'payer' &&
                (canSetUp ? (
                    <Button size="small" onClick={onSetUp}>
                        {GAP_COPY.setItUp}
                    </Button>
                ) : (
                    <Button size="small" onClick={onMessageOwner}>
                        {GAP_COPY.messageOwner}
                    </Button>
                ))}
        </div>
    );
};

// ── service template 缺 carrier 横幅（琥珀）——与 payer ID 横幅同源于 payerIdMapped=false，
// 但格子压根不存在：设置弹窗的 grid 就是 payer-config 本身、不插缺行（2026-09-15 拍板），
// 所以这里不进弹窗，直接把人送去 service template 页加 carrier。
// practice 视图（canAdd=false）：template 归被查的 provider，owner 改不了 → 只说事实不给按钮
export const TemplateGapNote: React.FC<{ carrier: string; canAdd?: boolean; onAdd?: () => void }> = ({
    carrier,
    canAdd,
    onAdd,
}) => (
    <div className={s.updateNote} style={{ alignItems: 'center' }}>
        <WarnIcon />
        <span className={s.noteBody}>
            {canAdd ? TEMPLATE_GAP_COPY.self(carrier) : TEMPLATE_GAP_COPY.other(carrier)}
        </span>
        {canAdd && (
            <Button size="small" onClick={onAdd}>
                {TEMPLATE_GAP_COPY.action}
            </Button>
        )}
    </div>
);

// ── team request 横幅（琥珀，owner 专属，practice-wide）——不点名（systemic, not personal），
// 是谁在等，打开 grid 后 chip 上的圆点自然指明
export const TeamRequestBanner: React.FC<{ count: number; onOpen: () => void }> = ({ count, onOpen }) => (
    <div className={s.updateNote} style={{ alignItems: 'center' }}>
        <RefreshIcon />
        <span className={s.noteBody}>{count === 1 ? TEAM_REQUEST_COPY.one : TEAM_REQUEST_COPY.many(count)}</span>
        <Button size="small" onClick={onOpen}>
            {TEAM_REQUEST_COPY.action}
        </Button>
    </div>
);

// ── card update 提示条（琥珀）——NOTICE 不带按钮：Re-check 就在下方 bar 上，第二个按钮是重复。
// 条件（cardUpdatedAt > checkedAt）由调用方判定；本 provider 复查过后自然消失
export const CardUpdateNote: React.FC<{ cardUpdatedAt: string }> = ({ cardUpdatedAt }) => (
    <div className={s.updateNote}>
        <RefreshIcon />
        <span className={s.noteBody}>{CARD_UPDATE_COPY(formatCheckedDate(cardUpdatedAt))}</span>
    </div>
);
